import { SelectProps } from 'antd/lib/select';
import { message, Select } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Option } from '../../../types';
import { getMessageInError } from '../../../hooks/fetch';
import { useWarehouseId, useWarehousesGet, WarehousesGetParams } from '../../../hooks/warehouses';
import NotFoundContent from '../NotFoundContent';

interface SelectWarehouseMulti extends SelectProps<number[], Option> {
  activeOnly?: boolean;
}

const SelectWarehouseMulti: React.FC<SelectWarehouseMulti> = ({ activeOnly, ...props }): JSX.Element => {
  const warehousesGet = useWarehousesGet();
  const warehouseId = useWarehouseId();

  const [options, setOptions] = useState<Option[]>([]);
  const [warehousesName, setWarehousesName] = useState<string>('');

  const fetch = () => {
    const params: WarehousesGetParams = { page: 1, limit: 999, orderBy: 'ASC', orderByColumn: 'name' };

    if (activeOnly) {
      params.status = activeOnly;
    }

    if (warehousesName) {
      params.name = warehousesName;
    }

    warehousesGet.fetch(params);
  };

  useEffect(() => {
    if (warehousesName && warehousesName.length > 1) {
      const id = setTimeout(fetch, 700);

      return () => clearTimeout(id);
    }

    fetch();

    return undefined;
  }, [warehousesName]);

  useEffect(() => {
    if (warehousesGet.data?.items.length) {
      setOptions(warehousesGet.data.items.map(({ name, id }): Option => ({ label: name, value: id })));
    }
  }, [warehousesGet.data]);

  const ref = useRef<boolean>(true);

  useEffect(() => {
    if (options.length && props.value && ref.current) {
      ref.current = false;
      const current = props.value.filter((item) => !options.map(({ value }) => +value).includes(item));

      if (current.length && !warehouseId.loading) {
        current.forEach((value) => {
          warehouseId.fetch(undefined, `${value}`)
            .then((res) => setOptions([
              ...options,
              { label: res?.name || '', value },
            ]));
        });
      }
    }
  }, [options, props.value]);

  useEffect(() => {
    if (warehousesGet.error) {
      message.error(getMessageInError(warehousesGet.error));
      warehousesGet.clearError();
    }
  }, [warehousesGet.error]);

  const filterOption = useCallback((input: string, option: Option | undefined) => (
    typeof option?.label === 'string' && option.label.toLowerCase().includes(input.toLowerCase())
  ), []);

  return (
    <Select
      mode="multiple"
      loading={warehousesGet.loading || warehouseId.loading}
      options={options}
      onSearch={(name) => setWarehousesName(name)}
      showSearch
      allowClear
      placeholder="Please select"
      filterOption={filterOption}
      notFoundContent={<NotFoundContent message="Warehouse not found." />}
      {...props}
    />
  );
};

SelectWarehouseMulti.defaultProps = {
  activeOnly: true,
};

export default SelectWarehouseMulti;
